import { SelectOption } from '@ardoq/select';
import { Locale } from '@ardoq/locale';
import { ArdoqId } from '@ardoq/api-types';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import {
  ReferenceAnswerValue,
  Fields,
  AdditionalFilterOptionsFunc,
  GetAISuggestions,
  FetchComponentOptionsWithQuery,
} from '../../types';
import { GetUserFeedbackReturn } from '../getUserFeedback';
import { getIdentifierStringFromReferenceAnswerValue } from '../questionOperations';
import { SurveyQuestionOptions } from '../QuestionOptions/SurveyQuestionOptions';
import { SurveyReadOnlyQuestion } from '../SurveyReadOnlyQuestion';

type SurveyListReferencesQuestionProps = {
  surveyId: string;
  questionId: string;
  componentId: ArdoqId | undefined;
  allowComponentCreation?: boolean;
  references: ReferenceAnswerValue[];
  readonly?: boolean;
  fields: Fields;
  identifierFieldName: string | undefined;
  userFeedback: GetUserFeedbackReturn;
  currentLocale: Locale;
  i18nLocale?: I18nLocale;
  shouldFetchReferenceSuggestions: boolean;
  hideReadOnlyLabel: boolean;
  questionLabel: string;
  additionalFilterOptions?: AdditionalFilterOptionsFunc;
  createReference: (
    newOption: SelectOption<string>[] | SelectOption<string>
  ) => void;
  deleteReference: (removedReference: ReferenceAnswerValue) => void;
  getAISuggestions: GetAISuggestions;
  fetchComponentOptionsWithQuery: FetchComponentOptionsWithQuery;
};

export const SurveyListReferencesQuestion = ({
  surveyId,
  questionId,
  componentId,
  allowComponentCreation,
  references,
  readonly,
  userFeedback,
  fields,
  identifierFieldName,
  currentLocale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  shouldFetchReferenceSuggestions,
  hideReadOnlyLabel,
  questionLabel,
  createReference,
  deleteReference,
  additionalFilterOptions,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
}: SurveyListReferencesQuestionProps) => {
  const getOptionValue = (ref: ReferenceAnswerValue) =>
    `${ref.referenceId}-${ref.component._id || ref.component.name}`;

  const currentSelected: SelectOption<string>[] = references.map(ref => {
    const identifierString = getIdentifierStringFromReferenceAnswerValue(
      ref,
      fields,
      identifierFieldName,
      currentLocale
    );
    return {
      value: getOptionValue(ref),
      label: `${ref.component.name} ${identifierString}`,
    };
  });

  const onChange = (
    newSelected: SelectOption<string>[] | SelectOption<string> | null
  ) => {
    if (!Array.isArray(newSelected)) return;

    const newOption = newSelected.find(
      selected =>
        !currentSelected.some(current => current.value === selected.value)
    );

    const removedReference =
      !newOption &&
      references.find(reference => {
        return !newSelected.some(
          selected => getOptionValue(reference) === selected.value
        );
      });

    if (newOption) {
      return createReference(newOption);
    }

    if (removedReference) {
      return deleteReference(removedReference);
    }
  };

  if (readonly && !currentSelected.length)
    return (
      <SurveyReadOnlyQuestion
        hideReadOnlyLabel={hideReadOnlyLabel}
        i18nLocale={i18nLocale}
      >
        {t(i18nLocale, 'surveysUi:noneSelected', 'None selected.')}
      </SurveyReadOnlyQuestion>
    );

  if (readonly && currentSelected.length)
    return (
      <SurveyReadOnlyQuestion
        hideReadOnlyLabel={hideReadOnlyLabel}
        i18nLocale={i18nLocale}
      >
        {currentSelected.map(selection => selection.label).join(', ')}
      </SurveyReadOnlyQuestion>
    );

  return (
    <div>
      <SurveyQuestionOptions
        surveyId={surveyId}
        questionId={questionId}
        componentId={componentId}
        isCreatable={allowComponentCreation}
        isMulti={true}
        value={currentSelected}
        userFeedback={userFeedback}
        fields={fields}
        identifierFieldName={identifierFieldName}
        currentLocale={currentLocale}
        i18nLocale={i18nLocale}
        shouldFetchReferenceSuggestions={shouldFetchReferenceSuggestions}
        additionalFilterOptions={additionalFilterOptions}
        onChange={onChange}
        getAISuggestions={getAISuggestions}
        fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
        questionLabel={questionLabel}
      />
    </div>
  );
};
